import { Typography, ToggleButton, Stack, Divider, RadioGroup, useTheme } from '@mui/material';
import { Radio, ToggleSelect } from '@perry-weather/component-library';
import { ABBREVIATIONS, POLICIES, POLICY_DATA_INTERVAL_ARRAY } from '../../../Constants/HistoricalReportConstants';

interface PoliciesProps {
  isAllSelected: boolean;
  isDateRangeValid: boolean;
  policiesDataInterval: string;
  selections: Map<string, string[]>;
  policySwitchAllTrigger: boolean[];
  styles: {
    chip: any;
  };
  handleSelectAllPolicies: () => void;
  handlePolicyDataIntervalChange: (newDataInterval: string) => void;
  handleSelectedPolicies: (policy: string, types: string[]) => void;
}

const HELPER_TEXT: Record<string, string> = {
  ['Detailed']: 'Detailed report of every individual policy event',
  ['Daily']: 'A daily summary for each policy',
  ['Weekly']: 'A weekly summary for each policy',
  ['Monthly']: 'A monthly summary for each policy',
  ['Overall Summary']: 'A single summary for each policy during the entire date range',
};

const Policies = (props: PoliciesProps) => {
  const {
    policySwitchAllTrigger,
    policiesDataInterval,
    isDateRangeValid,
    isAllSelected,
    selections,
    styles,
    handleSelectAllPolicies,
    handleSelectedPolicies,
    handlePolicyDataIntervalChange,
  } = props;

  const theme = useTheme();

  return (
    <>
      <Typography variant='h6' fontWeight='bold'>
        Policies
      </Typography>
      <Typography color='textSecondary' overflow='visible'>
        Select the weather policies you'd like to include in your report.
      </Typography>

      <ToggleButton
        selected={isAllSelected}
        value=''
        onChange={handleSelectAllPolicies}
        sx={{ ...styles.chip, margin: '14px 0' }}>
        Select All
      </ToggleButton>

      <Stack direction='row' flexWrap='wrap' gap='14px'>
        {POLICIES.map(item => {
          if (item.types) {
            return (
              <ToggleSelect
                key={item.value}
                value={item.value}
                title={item.label}
                header='Options'
                selectAll={policySwitchAllTrigger[0]}
                deselectAll={policySwitchAllTrigger[1]}
                options={item.types}
                abbreviations={ABBREVIATIONS}
                menuHeightAdjustment={2}
                autoMenuWidthProps={{
                  fontSize: '16px',
                  fontFamily:
                    "-apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif",
                  adjustment: 100,
                }}
                sx={{
                  text: { fontWeight: 'bold' },
                }}
                onChange={handleSelectedPolicies}
              />
            );
          } else {
            return (
              <ToggleButton
                key={item.value}
                selected={selections ? selections.has(item.value) : false}
                value={item.value}
                onChange={() => {
                  if (selections.has(item.value)) {
                    handleSelectedPolicies(item.value, []);
                  } else {
                    handleSelectedPolicies(item.value, [item.value]);
                  }
                }}
                sx={styles.chip}>
                {item.label}
              </ToggleButton>
            );
          }
        })}
      </Stack>

      <Divider style={{ width: '100%', marginTop: '24px', marginBottom: '14px' }} />

      <Typography variant='h6' fontWeight='bold'>
        Data Interval
      </Typography>
      <Typography color='textSecondary' overflow='visible'>
        Choose how you want your data summarized.
      </Typography>

      <Stack paddingX={2} gap={1}>
        <RadioGroup
          row
          value={policiesDataInterval}
          name='radio-data-interval'
          sx={{ gap: '18px' }}
          onChange={(_, value) => handlePolicyDataIntervalChange(value)}>
          {POLICY_DATA_INTERVAL_ARRAY.map(([interval]) => (
            <Radio key={interval} value={interval} label={interval} size='small' />
          ))}
        </RadioGroup>

        <Typography color='textSecondary' overflow='visible'>
          {HELPER_TEXT[policiesDataInterval]}
        </Typography>
      </Stack>

      {!isDateRangeValid && (
        <Typography color={theme.palette.error.light} overflow='visible' noWrap>
          Update your date range to use this interval.
        </Typography>
      )}
    </>
  );
};

export default Policies;
